/**
 * maintenance.ts
 *
 * Wires the maintenance store into the running app.
 *
 * The router guard in `router/maintenanceGuard.ts` covers navigations, but
 * a maintenance can start while a user is sitting still on one page. This
 * module supplies the other half: it polls remote configuration and, when
 * the answer changes, re-applies the same rule to the route the user is
 * already on.
 */
import { watch } from 'vue'
import { resolveMaintenanceRedirect } from '@/router/maintenanceGuard'
import { useAuthStore } from '@/stores/authStore'
import { useMaintenanceStore } from '@/stores/maintenanceStore'

/**
 * The slice of the router this module needs.
 *
 * Declared structurally rather than taking `Router` so a test can supply
 * the current path and a navigation spy without standing up a real router
 * and its twenty-odd unused methods.
 */
export interface MaintenanceRouter {
  currentRoute: { value: { path: string } }
  push: (path: string) => unknown
}

/**
 * Starts reading maintenance configuration and enforcing what it says.
 *
 * Call once at startup, after Pinia is installed and the remote
 * configuration source has been provided.
 *
 * @param router - The application router, used to move a user off a page
 *   they may no longer stay on.
 */
export function startMaintenanceEnforcement(router: MaintenanceRouter): void {
  const maintenanceStore = useMaintenanceStore()
  const authStore = useAuthStore()

  void maintenanceStore.refresh()
  maintenanceStore.startPolling()

  watch(
    () => maintenanceStore.isMaintenanceMode,
    (isMaintenanceMode) => {
      const redirect = resolveMaintenanceRedirect({
        path: router.currentRoute.value.path,
        isMaintenanceMode,
        isAdmin: authStore.isAdmin,
      })

      if (redirect) {
        void router.push(redirect)
      }
    },
  )
}
